import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, FileText, Scan, Share2 } from 'lucide-react';
import { QuickEstimateForm } from '@/components/quick-estimate/QuickEstimateForm';
import { EstimatePDFPreview } from '@/components/quick-estimate/EstimatePDFPreview';
import { useARMeasurement } from '@/hooks/useARMeasurement';
import { toast } from 'sonner';
import { Share } from '@capacitor/share';

export default function QuickEstimate() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [estimateData, setEstimateData] = useState<any>({});
  const [showPreview, setShowPreview] = useState(false);
  const { openARMeasure, isARAvailable } = useARMeasurement();

  if (!isMobile) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="p-6 max-w-md text-center">
          <h2 className="text-xl font-semibold mb-2">Mobile Only</h2>
          <p className="text-muted-foreground">
            Quick Estimate is optimized for mobile devices.
            Please use the full estimate builder on desktop.
          </p>
          <Button onClick={() => navigate('/jobs')} className="mt-4">
            Go to Jobs
          </Button>
        </Card>
      </div>
    );
  }

  const handleARMeasure = async () => {
    const result = await openARMeasure();
    if (result) {
      setEstimateData({
        ...estimateData,
        ...result,
      });
      toast.success('Measurements imported from AR');
    }
  };

  const handleShare = async () => {
    try {
      await Share.share({
        title: 'Quick Estimate',
        text: 'Check out this roofing estimate',
        url: window.location.href, // In production, this would be the PDF URL
        dialogTitle: 'Share Estimate',
      });
    } catch (error) {
      console.error('Share error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
        <div className="flex items-center gap-3 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-semibold">Quick Estimate</h1>
            <p className="text-sm text-muted-foreground">
              Generate instant estimate
            </p>
          </div>
          {showPreview && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleShare}
            >
              <Share2 className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {!showPreview ? (
          <>
            <QuickEstimateForm
              estimateData={estimateData}
              onChange={setEstimateData}
            />

            {/* AR Measurement Button */}
            {isARAvailable && (
              <Card className="p-4 mb-4">
                <Button
                  variant="outline"
                  className="w-full h-12"
                  onClick={handleARMeasure}
                >
                  <Scan className="w-5 h-5 mr-2" />
                  AR Measurement (Coming Soon)
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Use your camera to measure the roof automatically
                </p>
              </Card>
            )}
          </>
        ) : (
          <EstimatePDFPreview estimateData={estimateData} />
        )}
      </div>

      {/* Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t">
        {showPreview ? (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPreview(false)}
              className="flex-1"
            >
              Edit
            </Button>
            <Button
              onClick={handleShare}
              className="flex-1"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share PDF
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => setShowPreview(true)}
            className="w-full h-12"
            size="lg"
            disabled={!estimateData.roofSquares}
          >
            <FileText className="w-5 h-5 mr-2" />
            Generate Estimate
          </Button>
        )}
      </div>
    </div>
  );
}
